import type { ActionDefinition } from '../../types';
import type { ActionHandler, ActionContext } from '../../registry/ActionRegistry';

/**
 * Fill input action definition
 */
export interface FillInputAction extends ActionDefinition {
  type: 'fill-input';
  targetSelector: string;
  value: string;
  /** Typing speed in ms per character (0 = instant) */
  typingSpeed?: number;
  /** Timeout for finding element (ms) */
  timeout?: number;
}

/**
 * Handler for filling text inputs and textareas
 * Supports instant fill or character-by-character typing
 */
export class FillInputActionHandler implements ActionHandler<FillInputAction> {
  async execute(action: FillInputAction, context: ActionContext): Promise<void> {
    const { targetSelector, value, typingSpeed = 0, timeout = 5000 } = action;

    // Find element
    const element = await context.domAdapter.waitForElement(targetSelector, timeout);
    if (!element) {
      throw new Error(`Element not found: ${targetSelector}`);
    }

    // Verify it's an input or textarea
    if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) {
      throw new Error(`Element is not an input or textarea: ${targetSelector}`);
    }

    // Check if element is visible
    if (!context.domAdapter.isVisible(element)) {
      throw new Error(`Element not visible: ${targetSelector}`);
    }

    // Check if element is disabled
    if (context.domAdapter.isDisabled(element)) {
      throw new Error(`Element is disabled: ${targetSelector}`);
    }

    // Check if element is readonly
    if (context.domAdapter.isReadOnly(element)) {
      throw new Error(`Element is readonly: ${targetSelector}`);
    }

    // Focus the element
    context.domAdapter.focus(element);

    if (typingSpeed === 0) {
      // Instant fill
      context.domAdapter.fillInput(element, value);
    } else {
      // Character-by-character typing
      for (let i = 0; i < value.length; i++) {
        const partialValue = value.substring(0, i + 1);
        context.domAdapter.fillInput(element, partialValue);
        await context.timerAdapter.delay(typingSpeed);
      }
    }

    // Verify value was set correctly
    if (element.value !== value) {
      throw new Error(`Failed to set input value. Expected: "${value}", Got: "${element.value}"`);
    }
  }

  validate(action: FillInputAction): boolean {
    return Boolean(action.targetSelector && typeof action.value === 'string');
  }
}
